import { scanPhotoSaveUrl, scanPhotoFormObject, scanPhotoFormFields } from "@form/scanPhotoFormFields"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { object } from 'prop-types'
import { post, get } from '@util/xhrAPI'
import { useEffect, useState } from "react"
import { updateSterilizationCall, updateActiveRowCall } from "@store/slice/sterilizationSlice"
import { updateNotification } from '@store/slice/mainSlice'
import useStateUpdate from "@hook/useStateUpdate"
import useToggle from "@hook/useToggle"
import useFormData from "@hook/useFormData"
import Div from '@ui/layout/Div'
import Form from '@ui/elements/Form'
import Icon from '@ui/elements/Icon'
import useDeleteButton from '@hook/useDeleteButton'
import useDynamicForm from '@hook/useDynamicForm'
import Image from "@ui/elements/Image"
import SplideJs from "@ui/component/SplideJs"

const ScanPhotoBox = ({ scannedPhoto = { ...scanPhotoFormObject }, initObject = {} }) => {
  const [defaultValues] = useState(initObject)
  const dispatch = useDispatch()
  const [t] = useTranslation('sterilization')
  const deleteButton = useDeleteButton()
  const { errors, calls, activeRowCall, callsScanPhotos } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const initialObject = { ...scannedPhoto, sterilizationCall: activeRowCall.id, mission: mission.id, ...initObject }
  const [photoObject, setPhotoObject] = useState({ ...initialObject })

  const [updateFormObject, updateFormObjectProp] = useStateUpdate(setPhotoObject, photoObject)
  const [isLoading, toggleLoading] = useToggle(false)
  const renderFormData = useFormData()
  const toggleDynamicForm = useDynamicForm()

  const saveForm = async () => {
    toggleLoading()
    post(scanPhotoSaveUrl, renderFormData(photoObject), response => {
      const { code, errors, scanPhoto } = response
      if (200 === code) {
        let sterilizationCall = calls[photoObject['sterilizationCall']]
        const scanPhotos = { ...sterilizationCall.scansData.photos }
        scanPhotos[scanPhoto.id] = scanPhoto
        sterilizationCall = { ...sterilizationCall, scansData: { ...sterilizationCall.scansData, photos: { ...scanPhotos } } }
        dispatch(updateActiveRowCall(sterilizationCall))
        dispatch(updateSterilizationCall(sterilizationCall))
        if (scannedPhoto.id) {
          setPhotoObject({ ...scanPhoto, sterilizationCall: activeRowCall.id, mission: mission.id })
        } else {
          setPhotoObject({ ...scanPhotoFormObject, sterilizationCall: activeRowCall.id, mission: mission.id })
        }
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors[0]}`) }))
      }
      toggleLoading()
    })
  }

  const generateCallNests = () => {
    if (
      (scannedPhoto.isFirstScan || 'isFirstScan' === photoObject.scan)
      || !callsScanPhotos[activeRowCall.id]
      || (callsScanPhotos[activeRowCall.id] && !callsScanPhotos[activeRowCall.id].length)
    ) {
      return <></>
    }

    let photosList = callsScanPhotos[activeRowCall.id]
    if (photoObject.releatedNest && photoObject.releatedNest === scannedPhoto.releatedNest) {
      const reorderedPhotos = []
      for (let index = 0; index < photosList.length; index++) {
        const scanPhoto = photosList[index]
        if (photoObject.releatedNest === scanPhoto.id) {
          reorderedPhotos.unshift(scanPhoto)
        } else {
          reorderedPhotos.push(scanPhoto)
        }
      }
      photosList = reorderedPhotos
    }

    const photos = photosList.map(scanPhoto => {
      return <Image
        src={scanPhoto.photo}
        callback={() => updateFormObjectProp('releatedNest', scanPhoto.id)}
        otherProps={{ 'data-splide-lazy': scanPhoto.photo }}
        key={`realeated-nest-${scanPhoto.id}-${scannedPhoto.id || 'new-scan-photo'}`}
        cssFigureClass={photoObject.releatedNest === scanPhoto.id ? 'is-releated' : ''}
      />
    })

    return (
      <Div cssClass='releated-nest-zone'>
        <span className="label">{t('labels.releated_nest')}</span>
        <SplideJs slides={photos} cssClass='releated-nest-slider' />
        {photoObject.releatedNest
          && <Icon
            spanClass='cancel-nest-association'
            icon='icon-blocked'
            label={t('labels.cancel_nest_association')}
            callback={() => updateFormObjectProp('releatedNest', null)}
          />
        }
      </Div>
    )
  }

  useEffect(() => {
    if (photoObject.sterilizationCall !== activeRowCall.id) {
      setPhotoObject({ ...photoObject, sterilizationCall: activeRowCall.id, ...initObject })
    }
  }, [activeRowCall, initObject])

  useEffect(() => {
    if (JSON.stringify(defaultValues) !== JSON.stringify(initObject)) {
      setPhotoObject({ ...initialObject, ...initObject })
    }
  }, [initObject])

  const removeScanPhoto = () => {
    const { id, links } = scannedPhoto
    get(`${import.meta.env.VITE_API_URL}${links.remove}`, response => {
      const { code } = response
      if (code === 200) {
        let sterilizationCall = calls[photoObject['sterilizationCall']]
        const scanPhotos = { ...sterilizationCall.scansData.photos }
        delete scanPhotos[id]
        sterilizationCall = { ...sterilizationCall, scansData: { ...sterilizationCall.scansData, photos: { ...scanPhotos } } }
        dispatch(updateActiveRowCall(sterilizationCall))
        dispatch(updateSterilizationCall(sterilizationCall))
        dispatch(updateNotification({ type: 'is-success', message: t('messages.removed_successfuly', { title: document.name }) }))
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t('messages.error_occured') }))
      }
    })
  }

  return (
    <Div cssClass='scan-photo-box column is-4'>
      <Div cssClass='photo-box'>
        {scannedPhoto.id && deleteButton(null, scannedPhoto.id, removeScanPhoto)}
        <Form cssClass='scan-photo-form columns is-multiline'>
          {
            scanPhotoFormFields(
              photoObject,
              updateFormObject,
              updateFormObjectProp,
              saveForm,
              errors,
              isLoading,
              t,
              toggleDynamicForm,
              mission.missionSubjects,
              generateCallNests
            )
          }
        </Form>
      </Div>
    </Div>
  )
}

ScanPhotoBox.propTypes = {
  scannedPhoto: object,
  initObject: object
}

export default ScanPhotoBox